import {
    ENTITY_SERVICE_REGISTRY,
    EntityServiceEntry
} from 'features/core/shared/dto/dto-service/entity-service-registry';
import {CpProvisionAgentStatementService} from './service/api/cp-provision-agent-statement.service';
import {MvsCrudModeEnum} from 'features/core/shared/service/crud/mvs-crud-mode.enum';
import {
    CpProvisionAgentObjectComponent
} from './component/object-components/cp-provision-agent-object/cp-provision-agent-object.component';
import {
    CpProvisionTypeObjectComponent
} from './component/object-components/cp-provision-type-object/cp-provision-type-object.component';
import {
    CpProvisionContractObjectComponent
} from './component/object-components/cp-provision-contract-object/cp-provision-contract-object.component';
import {
    CpProvisionContractAgentObjectComponent
} from './component/object-components/cp-provision-contract-agent-object/cp-provision-contract-agent-object.component';
import {
    CpProvisionTypeMatrixObjectComponent
} from './component/object-components/cp-provision-type-matrix-object/cp-provision-type-matrix-object.component';
import {
    CpRunDetailContractRunComponent
} from './component/shared/cp-run-detail-contract-run/cp-run-detail-contract-run.component';
import {
    CpRunDetailMessageComponent
} from './component/shared/cp-run-detail-message/cp-run-detail-message.component';

/**
 * Entity service registry of the cp module (provision outgoing) — 04-frontend.md §8.
 *
 * ALL 33 cp entities are registered here. The REST routes `/cp/{camelPlural}` are
 * binding (04 §8) and match the @RequestMapping paths of the controllers.
 *
 * Completeness of the registration is part of the DoD: a referenced but not
 * registered alias was the failure mode of the legacy module (IST §7.2). Whoever uses a new
 * `cp.*` alias in a widget or `<mvs-object>` MUST register it here.
 *
 * Entries without `service` use route-only registration — the framework automatically
 * resolves a generic MvsCrudService for standard CRUD.
 *
 * OBC: SEVEN aliases own their own ObjectBaseComponent (two of them share one), the rest deliberately fall back
 * to the generic CRUD object (PAT §L.3 — OBCs only where they add business value).
 *
 *   1. `cp.ProvisionAgent`         -> CpProvisionAgentObjectComponent (04-frontend.md §7, mode = update).
 *   2. `cp.ProvisionType`          -> CpProvisionTypeObjectComponent — MANDATORY, not optional:
 *      `cp.ProvisionType` has ten same-module configuration children and is therefore a
 *      COMPLEX CONFIGURATION ENTITY. The rule (`fe_core_page`, `fe_core_config-page-audit`
 *      CONFIG-003/CONFIG-015) requires exactly this registration: without it, the row click
 *      on the config page falls back to the generic form and the ten children would not be
 *      maintainable. The guiding precedent is the identical situation in bd:
 *      `features/feature-bm/bd/bd.entity-provider.ts:33`
 *      (`['bd.DunningType', {..., objectComponent: [{component: DunningTypeObjectComponent}]}]`).
 *   3. `cp.ProvisionContract`      -> CpProvisionContractObjectComponent — A-5 withdrawn
 *      (requirement.md §7): the OBC for cp.ProvisionContract IS in scope, mode = update.
 *   4. `cp.ProvisionContractAgent` -> CpProvisionContractAgentObjectComponent — right pane of the
 *      Review Workbench on navItem `contractAgents` (requirement.md §4), mode = update.
 *   5. `cp.ProvisionTypeMatrix`    -> CpProvisionTypeMatrixObjectComponent, mode = update. The
 *      entry point is the inheritance-chain chip of the Beratersicht:
 *      `cp-provision-agent-configuration.component.html` calls
 *      `openObject('cp.ProvisionTypeMatrix', step.matrixId, …)`, which navigates with
 *      `uiMode = 'side'` into the LEFT drawer. Until this registration existed, that click landed
 *      in the generic form — an editing surface, for a question that is purely "what does this
 *      matrix contain?". The OBC answers it with two widgets (header + rows) and resolves through
 *      the entity registry, so the CALLER did not have to change at all.
 *   6. `cp.ProvisionContractRun`   -> CpRunDetailContractRunComponent, mode = read + update. READ-ONLY pane
 *      (outcome, Fehlergrund, bookings of the run) — the same component the run-detail "Verträge"
 *      tab hosts. Registered so that generic navigation (openObjectViaDialog / object navigation)
 *      no longer lands in the generic CRUD form, which offered the engine outcome as editable.
 *   7. `cp.ProvisionContractRunMessage` + `cp.ProvisionRunMessage` -> CpRunDetailMessageComponent,
 *      mode = read + update. READ-ONLY message pane of the run-detail tabs "Meldungen" / "Lauf-Meldungen";
 *      the component derives the message kind from the object type of the identifier. Engine
 *      messages are never edited — the generic form was editable and its template request failed.
 *
 *   Items 6 + 7 carry an explicit `read` entry next to `update` because the widget "Anzeigen" action
 *   opens with MvsCrudModeEnum.read (widget-type-provider-implementation-data.ts) and
 *   resolveComponentMapping (entity-service-registry.ts:35-66) matches only the exact mode, a
 *   viewType, or a condition-less default. A condition-less default was NOT used: it would also claim
 *   create and delete, which must keep falling through to the generic form. The panes are read-only
 *   and have no mode-dependent behaviour, so read and update render identically.
 *
 * This DEVIATION from 04-frontend.md §8 ("OBC only for cp.ProvisionAgent") is deliberate: the
 * specification collides there with the Complex Configuration Entity Rule, and the reference
 * module bd decides in favour of the rule.
 */
export const CP_ENTITY_PROVIDER = [
    {
        provide: ENTITY_SERVICE_REGISTRY,
        useValue: [

            // --- Agent / provision account: own OBC (04-frontend.md §7) ---
            ['cp.ProvisionAgent', {
                route: '/cp/provisionAgents',
                objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionAgentObjectComponent}]
            }],

            // --- Provision type: COMPLEX Configuration Entity (ten configuration children),
            //     therefore with its own OBC + left navigation (CONFIG-003/CONFIG-015) ---
            ['cp.ProvisionType', {
                route: '/cp/provisionTypes',
                objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionTypeObjectComponent}]
            }],

            // --- Configuration children of the provision type ---
            ['cp.ProvisionTypeGroup', {route: '/cp/provisionTypeGroups'}],
            ['cp.ProvisionLiabilityType', {route: '/cp/provisionLiabilityTypes'}],
            ['cp.ProvisionTypeProvisionBase', {route: '/cp/provisionTypeProvisionBases'}],
            ['cp.ProvisionTypeComponent', {route: '/cp/provisionTypeComponents'}], // NEU (WP-E13)
            ['cp.ProvisionTypeContractEvent', {route: '/cp/provisionTypeContractEvents'}], // NEU (WP-E13)
            ['cp.ProvisionTypeContractEventPriceReason', {route: '/cp/provisionTypeContractEventPriceReasons'}], // NEU (WP-E13)
            // Assignment conditions: named condition sets ("Bedingungssatz") and their conditions ("Bedingung"),
            // referenced by the six assignment families; route-only generic CRUD
            ['cp.ProvisionAssignmentConditionSet', {route: '/cp/provisionAssignmentConditionSets'}],
            ['cp.ProvisionAssignmentCondition', {route: '/cp/provisionAssignmentConditions'}],
            ['cp.ProvisionTypeContractEligibility', {route: '/cp/provisionTypeContractEligibilities'}],
            // K-18 — per-component contract relevance, deliberately separate from K-05 (01-requirement.md §6)
            ['cp.ProvisionTypeComponentEligibility', {route: '/cp/provisionTypeComponentEligibilities'}],
            ['cp.ProvisionTypeContractProvisionBase', {route: '/cp/provisionTypeContractProvisionBases'}],
            ['cp.ProvisionTypeContractLiability', {route: '/cp/provisionTypeContractLiabilities'}],
            // K-17 — calculated agent rules; route-only, exactly like ProvisionTypeMatrixAssignment
            ['cp.ProvisionTypeCalculatedAgent', {route: '/cp/provisionTypeCalculatedAgents'}],

            // --- Provision matrix: own OBC, opened by the inheritance-chain chip of the
            //     Beratersicht in the left drawer (uiMode = 'side') ---
            ['cp.ProvisionTypeMatrix', {
                route: '/cp/provisionTypeMatrices',
                objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionTypeMatrixObjectComponent}]
            }],
            ['cp.ProvisionTypeMatrixAgent', {route: '/cp/provisionTypeMatrixAgents'}],
            ['cp.ProvisionTypeMatrixAssignment', {route: '/cp/provisionTypeMatrixAssignments'}],

            // --- Booking types / booking events ---
            ['cp.ProvisionAgentTransactionType', {route: '/cp/provisionAgentTransactionTypes'}],
            ['cp.ProvisionContractTransactionType', {route: '/cp/provisionContractTransactionTypes'}],
            ['cp.ProvisionTypeTransactionEvent', {route: '/cp/provisionTypeTransactionEvents'}],

            // --- Agent master data and accounts ---
            ['cp.ProvisionAgentGroup', {route: '/cp/provisionAgentGroups'}],
            ['cp.ProvisionAgentBalance', {route: '/cp/provisionAgentBalances'}],
            ['cp.ProvisionAgentTransaction', {route: '/cp/provisionAgentTransactions'}],
            ['cp.ProvisionAgentReserveCoverage', {route: '/cp/provisionAgentReserveCoverages'}],

            // --- Runtime: provision run ---
            ['cp.ProvisionRun', {route: '/cp/provisionRuns'}],
            // Final agent statements (Provisionsabrechnung): read/list + reissue/download; no OBC yet
            ['cp.ProvisionAgentStatement', {route: '/cp/provisionAgentStatements', service: CpProvisionAgentStatementService}],

            // --- Runtime: contract provisions ---
            ['cp.ProvisionContract', {
                route: '/cp/provisionContracts',
                objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionContractObjectComponent}]
            }],
            ['cp.ProvisionContractAgent', { // renamed from the former liability entity, WP-E5
                route: '/cp/provisionContractAgents',
                objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionContractAgentObjectComponent}]
            }],
            ['cp.ProvisionContractTransaction', {route: '/cp/provisionContractTransactions'}],
            // Read-only pane (outcome is engine-owned, never edited) — see TSDoc item 6
            ['cp.ProvisionContractRun', {
                route: '/cp/provisionContractRuns',
                objectComponent: [
                    {mode: MvsCrudModeEnum.read, component: CpRunDetailContractRunComponent},
                    {mode: MvsCrudModeEnum.update, component: CpRunDetailContractRunComponent}
                ]
            }],

            // --- Messages: read-only pane, one component for both kinds — see TSDoc item 7 ---
            ['cp.ProvisionContractRunMessage', {
                route: '/cp/provisionContractRunMessages',
                objectComponent: [
                    {mode: MvsCrudModeEnum.read, component: CpRunDetailMessageComponent},
                    {mode: MvsCrudModeEnum.update, component: CpRunDetailMessageComponent}
                ]
            }],
            ['cp.ProvisionRunMessage', {
                route: '/cp/provisionRunMessages',
                objectComponent: [
                    {mode: MvsCrudModeEnum.read, component: CpRunDetailMessageComponent},
                    {mode: MvsCrudModeEnum.update, component: CpRunDetailMessageComponent}
                ]
            }],

        ] as EntityServiceEntry[],
        multi: true
    }
];
